import { useState } from 'react'
import { estimateFailureList } from '@/features/consignment-form/estimate'
import type { Unpriced } from '@/features/consignment-form/estimate'
import type { useEstimates } from '@/features/consignment-form/hooks/use-estimates'
import type { useItemEditor } from '@/features/consignment-form/hooks/use-item-editor'
import type { Item } from '@/features/consignment-form/schema'

type Props = {
  estimates: Pick<ReturnType<typeof useEstimates>, 'estimates' | 'estimateItem' | 'status'>
  editor: Pick<ReturnType<typeof useItemEditor>, 'index' | 'save' | 'close' | 'remove'>
  rows: readonly Item[]
}

/**
 * A row is kept only with its price: the guest may have to prepay, which needs the estimate. A Save that
 * cannot be priced leaves the editor open, and every missing price is listed for the error banner.
 */
export function useItemPricing({ estimates, editor, rows }: Props) {
  const [unpriced, setUnpriced] = useState<Unpriced>()
  // Bumped whenever the guest should be taken to the estimate banner.
  const [notice, setNotice] = useState(0)
  const pointToBanner = () => setNotice((count) => count + 1)

  const save = async (values: Item) => {
    const result = await estimates.estimateItem(values)
    if (result.status === 'ready') {
      setUnpriced(undefined)
      editor.save(values)
      return
    }
    if (result.status === 'unavailable') {
      setUnpriced({ index: editor.index, failure: result })
      pointToBanner()
    }
  }

  const close = () => {
    setUnpriced(undefined)
    editor.close()
  }

  const remove = () => {
    setUnpriced(undefined)
    editor.remove()
  }

  /** True, pointing to the banner, while a row has no price to send. */
  const holdsSubmit = () => {
    if (estimates.status !== 'unavailable') return false
    pointToBanner()
    return true
  }

  return {
    failures: estimateFailureList(rows, estimates.estimates, unpriced),
    notice,
    save,
    close,
    remove,
    holdsSubmit,
  }
}
